import React, { MutableRefObject, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { DotLegalButton, DotLegalPrettyScrollbar, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalTextFieldWithSnippetsStyles } from "./DotLegalTextFieldWithSnippets.styles";
import { isNullOrWhitespace } from "../../stringOperations";
import { useTranslation } from "../../../localization/useTranslation";

// Temp types -> should be removed when we can export types through our library
export type inputVariant = "standard" | "outlined";
export type textDecoration = "line-through";

// Temp props -> should be removed when we can export types through our library
interface DotLegalTextFieldProps {
    label: string;
    value: string | null;
    disabled?: boolean;
    onChange?: (value: string) => void;
    multiline?: boolean;
    rows?: number;
    placeholder?: string;
    errorText?: string;
    size?: "small" | "medium";
    toolTipText?: string;
    isLoading?: boolean;
    handleNumber?: boolean;
    noMargin?: boolean;
    debounce: boolean;
    helperText?: string;
    hasError?: boolean;
    variant?: inputVariant;
    id?: string;
    alwaysShowUnderline?: boolean;
    textDecoration?: textDecoration;
    inputRef?: MutableRefObject<HTMLTextAreaElement | undefined>;
    pdfClassName?: string;
    isPdf?: boolean;
}

export interface IDotLegalTextFieldWithSnippets extends DotLegalTextFieldProps {
    snippets: Array<string>;
    error: string;
    select: string;
    disableValidation?: boolean;
    limitHeight?: boolean;
    children?: React.ReactElement;
    canAddAll?: boolean;
    // Drops the frame around the field and its suggestions, for a field that already sits in a box of its own.
    hideFrame?: boolean;
}

function DotLegalTextFieldWithSnippets(props: IDotLegalTextFieldWithSnippets) {
    const styles = useDotLegalTextFieldWithSnippetsStyles(props);
    const inputRef = useRef<HTMLTextAreaElement>();
    const { translateString } = useTranslation();

    function insertAllSnippets() {
        const tempIndex = inputRef.current!.selectionStart;

        const snippetsToInsert = props.snippets.map((snippet, index) => {
            return `${isNullOrWhitespace(props.value ?? undefined) && index === 0 ? snippet : `\n${snippet}`}`;
        });

        const temp =
            props.value && inputRef.current
                ? props.value.slice(0, tempIndex) + snippetsToInsert.join("") + props.value.slice(tempIndex)
                : snippetsToInsert.join("");
        props.onChange!(temp!);
        inputRef.current?.focus();
    }

    const textField = <DotLegalTextField inputRef={inputRef} multiline variant={"standard"} noMargin {...props} />;
    if (props.isPdf) {
        return textField;
    }

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.container}>
                <Box sx={styles.margin}>
                    {textField}

                    {props.snippets.length > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={styles.snippetHeader}>{props.select}</Typography>
                            {props.canAddAll && !props.disabled && (
                                <DotLegalButton onClick={() => insertAllSnippets()} size="small" buttonType="linkButton">
                                    {translateString("selectAll")}
                                </DotLegalButton>
                            )}
                        </Box>
                    )}

                    <DotLegalPrettyScrollbar sx={styles.snippetScrollArea}>
                        <Box sx={styles.snippetContainer}>
                            {props.snippets.map((snippet) => {
                                return (
                                    <Box sx={styles.snippets} key={snippet}>
                                        <DotLegalButton
                                            disabled={props.disabled}
                                            onClick={() => {
                                                const tempIndex = inputRef.current!.selectionStart;
                                                const snippetToInsert = `${isNullOrWhitespace(props.value ?? undefined) ? snippet : `\n${snippet}`}`;
                                                const temp =
                                                    props.value && inputRef.current
                                                        ? props.value.slice(0, tempIndex) + snippetToInsert + props.value.slice(tempIndex)
                                                        : snippetToInsert;
                                                props.onChange!(temp!);
                                                inputRef.current?.focus();
                                            }}
                                            size="small"
                                            buttonType="secondary"
                                        >
                                            {snippet}
                                        </DotLegalButton>
                                    </Box>
                                );
                            })}
                            {props.children}
                        </Box>
                    </DotLegalPrettyScrollbar>
                </Box>
            </Box>
            {props.error && <Typography sx={styles.error}>{props.error}</Typography>}
        </Box>
    );
}

export default DotLegalTextFieldWithSnippets;
